/**
 * What has happened to a row, beside the row.
 *
 * **Newest first, which is the endpoint's own order** -- the same arrangement
 * the comments beside it use, and for the same reason: a list that grows at the
 * top needs no scroll anchoring to keep the reader's place.
 *
 * **Grouped the way the database's activity history is**: under a heading per
 * day, with back-to-back entries by the same person doing the same thing folded
 * into one line that opens onto each change.
 *
 * **One rendering for twenty-seven field types.** A change reads the way the
 * grid reads a cell, because it goes through the same `toHumanReadable` --
 * against the field as it was when the change was made rather than as it is
 * now. See `history.ts`, which is where all of that lives and none of it is
 * React.
 *
 * Read-only. Reverting a change is offered by the database-wide activity
 * history, which can address a whole operation or a whole batch at once; a
 * control here would only ever reach one entry of one row.
 */
import { useState } from 'react'
import { ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { Field } from '@/api/types'
import { useSessionStore } from '@/features/auth/session-store'
import { mergedPages } from '@/shared/lib/paged'
import { cn } from '@/shared/lib/utils'
import { whenText } from '@/shared/lib/when'
import { Button } from '@/shared/ui/button'
import { Skeleton } from '@/shared/ui/skeleton'
import type { FieldContext } from '../fields/behavior/behavior'
import { HistoryChanges } from './history-changes'
import {
  changedNames,
  historyChanges,
  historyDays,
  historyPhrase,
  isWorthShowing,
  useRowHistory,
  type HistoryGroup,
} from './history'

export function HistoryPanel({
  tableId,
  rowId,
  fields,
  context,
}: {
  tableId: number
  rowId: number
  /** Every field of the table, hidden ones included: this is about the row. */
  fields: readonly Field[]
  context: FieldContext
}) {
  const { t } = useTranslation()
  const history = useRowHistory(tableId, rowId)

  const entries = mergedPages(history.data)
    .map((entry) => ({ entry, changes: historyChanges(entry, fields, context) }))
    .filter(({ entry, changes }) => isWorthShowing(entry, changes))
  const days = historyDays(entries, new Date())

  return (
    <section aria-label={t('history.plural')} className="flex min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {history.isPending ? (
          <div className="space-y-3 py-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-2/3" />
          </div>
        ) : days.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">{t('history.none')}</p>
        ) : (
          days.map((day) => (
            <section key={day.heading.key} className="mb-4">
              <h3 className="sticky top-0 bg-background py-1 text-xs font-medium text-muted-foreground">
                {day.heading.kind === 'on'
                  ? day.heading.text
                  : t(`activity.${day.heading.kind}`)}
              </h3>
              <ul className="space-y-2">
                {day.groups.map((group) => (
                  <HistoryRow key={group.key} group={group} context={context} />
                ))}
              </ul>
            </section>
          ))
        )}

        {history.hasNextPage && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-2 w-full"
            disabled={history.isFetchingNextPage}
            onClick={() => {
              void history.fetchNextPage()
            }}
          >
            {t('history.more')}
          </Button>
        )}
      </div>
    </section>
  )
}

function HistoryRow({ group, context }: { group: HistoryGroup; context: FieldContext }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const userId = useSessionStore((state) => state.user?.id)

  // Every entry in a group shares who, what and how, so the newest answers.
  const { entry } = group.entries[0]
  const count = group.entries.length
  const names = changedNames(group.entries)

  // The workspace's list first, the entry's own name second. The entry carries
  // the name that was current when the change was made, which is what keeps it
  // readable after the account has gone -- but while the account is still here,
  // somebody who has been renamed should read as who they are now.
  const who =
    entry.user.id === userId
      ? t('history.you')
      : (context.resolveUserName?.(entry.user.id) ?? entry.user.name)

  const phrase = historyPhrase(entry.action_type)

  return (
    <li className="rounded border">
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full min-w-0 items-start gap-2 p-2 text-left"
        onClick={() => setOpen(!open)}
      >
        <ChevronRight
          className={cn(
            'mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform',
            open && 'rotate-90',
          )}
          aria-hidden
        />
        <span className="min-w-0">
          <span className="text-sm">
            <span className="font-medium">{who}</span>{' '}
            {phrase === null
              ? // Named rather than described. An action nobody here recognises
                // is still a record of something, and calling it an update would
                // be a guess about what it did.
                t('history.did', { action: entry.action_type })
              : t(`history.${phrase}`)}
            {count > 1 && <> · {t('history.times', { count })}</>}
            {/* An undone or redone change is a change in its own right, and its
                before and after read in the direction it actually went. */}
            {entry.action_command_type === 'UNDO' && <> · {t('history.undone')}</>}
            {entry.action_command_type === 'REDO' && <> · {t('history.redone')}</>}
            {/* Platform staff signed in as the user. Their realm subject, not a
                name: nothing here can resolve it. */}
            {entry.impersonator_sub && (
              <> · {t('history.impersonatedBy', { sub: entry.impersonator_sub })}</>
            )}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {names.length > 0 && <>{names.join(', ')} · </>}
            {whenText(entry.timestamp)}
          </span>
        </span>
      </button>

      {open && (
        <ul className="space-y-3 border-t px-2 py-2 pl-8">
          {group.entries.map((shown) => (
            <li key={shown.entry.id}>
              {count > 1 && (
                <p className="text-xs text-muted-foreground">
                  {whenText(shown.entry.timestamp)}
                </p>
              )}
              <HistoryChanges changes={shown.changes} />
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}
